import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Attempting login to:', `${API_URL}/api/auth/login`);
      
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      console.log('Login response status:', res.status);
      
      // Handle non-OK responses first based on status code
      // This avoids the "body already consumed" issue
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error('Username o password non validi');
        } else if (res.status === 422) {
          throw new Error('Dati di login non validi');
        } else if (res.status >= 500) {
          throw new Error('Errore del server. Riprova più tardi.');
        } else {
          throw new Error('Errore di autenticazione');
        }
      }
      
      // Only parse JSON for successful responses
      let data;
      try {
        data = await res.json();
      } catch (parseError) {
        console.error('Failed to parse success response:', parseError);
        throw new Error('Errore nella risposta del server');
      }

      // Check if localStorage is available (Safari private mode may block it)
      try {
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
        
        // Verify the token was saved
        const savedToken = localStorage.getItem('admin_token');
        if (!savedToken) {
          throw new Error('Token non salvato - verifica le impostazioni privacy del browser');
        }
        
        console.log('Token saved successfully');
      } catch (storageError) {
        console.error('localStorage error:', storageError);
        throw new Error('Impossibile salvare la sessione. Disabilita la navigazione privata o controlla le impostazioni privacy.');
      }
      
      // Redirect to admin
      navigate('/admin');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0f0f1a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_web-audit-tool-7/artifacts/o8y9x9lm_Logo%20Smart%20Info%20CYBER%20Red.png" 
            alt="Smart Info Cyber Security" 
            className="h-32 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">Pannello Admin</h1>
          <p className="text-gray-400 mt-2">Accedi per gestire servizi e prezzi</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="bg-[#1a1a2e] rounded-2xl p-8 border border-gray-800">
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-5">
            {/* Username */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-red-500 focus:outline-none transition-colors"
                  placeholder="Inserisci username"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-red-500 focus:outline-none transition-colors"
                  placeholder="Inserisci password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-8 py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Accesso in corso...
              </>
            ) : (
              <>
                <Lock className="h-5 w-5" />
                Accedi
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-gray-600 text-sm mt-8">
          © {new Date().getFullYear()} Smart Info Cyber Security
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
